import "amazon-connect-streams";
import { getLogger } from "@expert/logging";

import type { AmazonConnectChatSDK } from "@/amazon-connect/chat/amazonConnectChatSdk";

import withCallbacks from "@/amazon-connect/withCallbacks";

/**
 * Extracts and validates the contact state timestamp with safety checks.
 * This is useful for getting a fallback "assignedAt" time for chat contacts.
 */
export function getFallbackAssignedAtTime(contact: connect.Contact): Date | undefined {
    const contactState = contact.getState();

    // Only extract timestamp from accepted/connected states
    // ! important to include CONNECTING as assigned time is set when contact is accepted, which is before CONNECTED state
    const isAcceptedOrConnected =
        contactState.type === connect.ContactStateType.CONNECTING ||
        contactState.type === connect.ContactStateType.CONNECTED;

    if (!isAcceptedOrConnected) {
        return undefined;
    }

    const stateTimestamp = contactState.timestamp;

    // Basic validation for extra safety
    if (stateTimestamp && stateTimestamp instanceof Date && !Number.isNaN(stateTimestamp.getTime())) {
        return stateTimestamp;
    }

    return undefined;
}

/**
 * True when a contact is in a terminal state where ChatJS send/event APIs must not be called.
 *
 * Calling `sendMessage`/`sendEvent` on an ended ChatJS session throws inside the vendor request
 * handler ("Cannot set properties of undefined (setting 'metadata')") — surfaced as an uncaught
 * promise rejection — and logs a benign "Ignoring ... API bec chat has ended" warning. Guarding on
 * this before invoking ChatJS keeps chat teardown quiet without changing happy-path send behavior.
 *
 * Returns `false` when the state cannot be determined (missing/throwing `getState`) so that only a
 * confirmed terminal state suppresses a send.
 */
export function isContactEnded(contact: connect.Contact): boolean {
    let stateType: connect.ContactStateType | undefined;
    try {
        stateType = contact.getState?.()?.type;
    } catch {
        return false;
    }

    switch (stateType) {
        case connect.ContactStateType.ENDED:
        case connect.ContactStateType.MISSED:
        case connect.ContactStateType.REJECTED:
        case connect.ContactStateType.ERROR:
            return true;
        default:
            return false;
    }
}

/**
 * Ends a chat contact: destroys agent connection and cleans up ChatJS.
 * Used for completing/missed/stale chats. Not for wrapup (use `wrapupTask`).
 */
export async function endChatContact(
    contact: connect.Contact,
    chatSdk: AmazonConnectChatSDK | undefined,
    logger: ReturnType<typeof getLogger>,
): Promise<void> {
    const endLogger = logger.child({
        contactId: contact.contactId,
        action: "endChatContact",
    });

    try {
        const agentConnection = contact.getAgentConnection();
        if (agentConnection) {
            await withCallbacks((callbacks) => agentConnection.destroy(callbacks));
            endLogger.debug("Agent connection destroyed");
        }
    } catch (error) {
        endLogger.warn({ error }, "Failed to destroy agent connection - contact may no longer exist");
    }

    // Do NOT call contact.clear() for chat - agentConnection.destroy() automatically removes
    // the contact from the agent workspace, so clear() always fails with ContactNotFoundException

    if (chatSdk) {
        chatSdk.cleanupConnection(contact);
    }
}
